import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { XCircle, AlertTriangle, RefreshCw, Send } from 'lucide-react';

interface FailedStepProps {
  amount: number;
  onRetry: () => void;
  onSubmitTxHash: (txHash: string) => void;
}

export function FailedStep({ amount, onRetry, onSubmitTxHash }: FailedStepProps) {
  const [txHash, setTxHash] = useState('');

  const handleSubmit = () => {
    if (txHash.trim()) {
      onSubmitTxHash(txHash.trim());
    }
  };

  return (
    <div className="space-y-6 text-center">
      {/* Error Icon */}
      <div className="relative mx-auto w-24 h-24">
        <div className="absolute inset-0 rounded-full bg-destructive/10" />
        <XCircle className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-14 w-14 text-destructive" />
      </div>

      {/* Title */}
      <div className="space-y-2">
        <h3 className="font-bold text-2xl">Deposit Not Confirmed</h3>
        <p className="text-muted-foreground">
          We couldn't verify your {amount} USDT deposit.
        </p>
      </div>

      {/* Possible Reasons */}
      <Card className="bg-destructive/5 border-destructive/20 text-left">
        <CardContent className="py-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <div className="space-y-3">
              <p className="text-sm font-medium">Possible reasons:</p>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Transaction was sent on wrong network (not TRC20)</li>
                <li>• Wrong token was sent (not USDT)</li>
                <li>• Amount sent doesn't match expected amount</li>
                <li>• Transaction hasn't been broadcast yet</li>
                <li>• Network congestion causing delays</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* TXID Input for Support */}
      <div className="space-y-3 text-left">
        <p className="text-sm text-muted-foreground">
          If you believe this is an error, provide your transaction hash (TXID) for manual review:
        </p>
        <div className="flex gap-2">
          <Input
            placeholder="Enter your TXID"
            value={txHash}
            onChange={(e) => setTxHash(e.target.value)}
            className="text-sm"
          />
          <Button 
            variant="outline" 
            onClick={handleSubmit}
            disabled={!txHash.trim()}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Action Button */}
      <Button 
        className="w-full" 
        size="lg"
        onClick={onRetry}
      >
        <RefreshCw className="h-4 w-4 mr-2" />
        Try Again
      </Button>
    </div>
  );
}
